import React, { useEffect, useState } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  UpdateWaiversFilled,
  WaiverSignedEmailBody,
  adultWaiverEmailBody,
  arePassengerWaiversComplete,
  checkAndUpdateStatus,
  getBookingOnce,
  getWaiverTemplateByType,
  getListingByID,
  getRequiredPassengerWaivers,
  getUserByID,
  sendEmail,
  sendSMS,
  updateBookingStatus,
  updateWaiversFilled,
} from "../utils";
import {
  get,
  getDatabase,
  push,
  ref,
  serverTimestamp,
  set,
  update,
} from "firebase/database";
import SignatureCanvas from "react-signature-canvas";
import { ThreeDots } from "react-loader-spinner";

export default function TripWaivers() {
  const { dynamicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [adultFormResponses, setAdultFormResponses] = useState([]);
  const [adultWaiverQuestions, setAdultWaiverQuestions] = useState([]);
  const [userData, setUserData] = useState([]);
  const [bookingData, setBookingData] = useState([]);
  const [renterData, setRenterData] = useState([]);
  const [ownerData, setOwnerData] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState([]);
  const [renterEmail, setRenterEmail] = useState([]);
  const [inquiryDetails, setInquiryDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [listingData, setListingData] = useState(null);
  const [isBoatCaptain, setIsBoatCaptain] = useState(null);
  const [captainAcknowledgeLoading, setCaptainAcknowledgeLoading] =
    useState(false);
  const [signatureDateTime, setSignatureDateTime] = useState("");
  const signatureRef = React.useRef();

  const requiredPassengerWaivers = getRequiredPassengerWaivers(
    bookingData,
    listingData
  );
  const passengerWaiversComplete = arePassengerWaiversComplete(
    bookingData,
    listingData
  );

  const handleCaptainAcknowledge = async () => {
    if (!passengerWaiversComplete) {
      return;
    }

    setCaptainAcknowledgeLoading(true);
    try {
      await updateBookingStatus(dynamicId, "Waiver Filled");
      navigate(`/dashboard/event/${dynamicId}`);
    } catch (error) {
      console.error("Error acknowledging captain waiver:", error);
    } finally {
      setCaptainAcknowledgeLoading(false);
    }
  };

  const handleSignatureBegin = () => {
    if (!signatureDateTime) {
      const now = new Date();
      setSignatureDateTime(
        now.toLocaleString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      );
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768; // Adjust the breakpoint as needed

      setIsMobile(isMobile);
    };

    // Initial setup
    handleResize();

    // Attach event listener for window resize
    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const [signatureUrl, setSignatureFile] = useState("");
  const handleSaveSignature = async () => {
    if (signatureRef.current.isEmpty()) {
      console.error("Signature is empty");
      return;
    }

    const signatureImage = signatureRef.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    const signatureFile = dataURLtoFile(signatureImage, "signature.png");
    await handleImageUpload("Signature", [signatureFile]);
  };

  const handleImageUpload = async (question, files) => {
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/upload-images`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        const filenames = data.file_names;

        if (question == "Signature") {
          setSignatureFile(
            `${process.env.REACT_APP_BASE_URL}/images/` + filenames
          );
        }
        const existingResponseIndex = adultFormResponses.findIndex(
          (response) => response.Question === question
        );

        if (existingResponseIndex !== -1) {
          const updatedResponses = [...adultFormResponses];
          updatedResponses[existingResponseIndex].Answer =
            question === "Signature"
              ? `${process.env.REACT_APP_BASE_URL}/images/` + filenames
              : filenames;
          setAdultFormResponses(updatedResponses);
          console.log("updatedResponses");
          console.log(updatedResponses);
        } else {
          setAdultFormResponses([
            ...adultFormResponses,
            {
              Question: question,
              Answer:
                question === "Signature"
                  ? `${process.env.REACT_APP_BASE_URL}/images/` + filenames
                  : filenames,
              type: "signature",
            },
          ]);
          console.log("adultFormResponses");
          console.log(question);
          console.log(adultFormResponses);
        }
      } else {
        console.error("Error uploading images:", response.statusText);
      }
    } catch (error) {
      console.error("Error uploading images:", error);
    }
  };

  const handleAdultInputChange = (question, answer) => {
    if (question === "date" || question === "country") {
      // Handle date and country inputs
      const updatedResponses = [...adultFormResponses];
      const existingResponseIndex = updatedResponses.findIndex(
        (response) => response.Question === question
      );

      if (existingResponseIndex !== -1) {
        updatedResponses[existingResponseIndex].Answer = answer;
      } else {
        updatedResponses.push({ Question: question, Answer: answer });
      }

      setAdultFormResponses(updatedResponses);
    } else {
      const existingResponseIndex = adultFormResponses.findIndex(
        (response) => response.Question === question
      );

      if (existingResponseIndex !== -1) {
        const updatedResponses = [...adultFormResponses];
        updatedResponses[existingResponseIndex].Answer = answer;
        setAdultFormResponses(updatedResponses);
      } else {
        setAdultFormResponses([
          ...adultFormResponses,
          { Question: question, Answer: answer },
        ]);
      }
    }
    console.log("adultFormResponses");
    console.log(adultFormResponses);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    var newPushKey = "";
    setLoading(true);
    try {
      await handleSaveSignature();
      // Define the path in the Firebase database
      const updatedFormResponses = await new Promise((resolve) => {
        setAdultFormResponses((prevResponses) => {
          const responses = [...prevResponses];
          resolve(responses);
          return responses;
        });
      });

      const database = getDatabase();
      const adultTripDataRef = ref(
        database,
        `bookings/${dynamicId}/tripData/waivers/Adult Waiver`
      );
      const adultsQuestionsArray = updatedFormResponses.map((response) => {
        let answer;
        if (response.type === "signature") {
          answer = response.Answer;
        } else if (response.type === "images") {
          // For images, use an array of URLs
          answer = response.Answer.map((image) => ({ url: image }));
        } else {
          // For other types, use the answer directly
          answer = response.Answer;
        }

        return {
          question:
            adultWaiverQuestions.find((q) => q.id === response.Question)
              ?.question || response.Question,
          answer,
        };
      });

      const newPushRef = await push(adultTripDataRef, {
        waiverName: "Adult Waiver",
        questions: adultsQuestionsArray,
        signedAt: serverTimestamp(),
      });
      newPushKey = newPushRef.key;

      await updateWaiversFilled(dynamicId, "adult");

      const subject = "Congratulations! Adult Passenger Waiver Signed";
      const WaiverName = "Adult Passenger Waiver";
      const url = `${process.env.REACT_APP_BASE_URL}/my-waiver/` + newPushKey;
      let name = updatedFormResponses
        .filter((response) => [0, 1, 2].includes(response.Question)) // Filter responses for questions 0, 1, and 2
        .map((response) => response.Answer.trim()) // Trim each response before mapping
        .join(" "); // Join the trimmed answers into a single string with a space between them

      const emailBody = await WaiverSignedEmailBody(
        WaiverName,
        name,
        url,
        newPushKey
      );
      sendEmail("waivers@bbrboatrentals.com", subject, emailBody);
      sendEmail(ownerEmail, subject, emailBody);
      sendEmail(renterEmail, subject, emailBody);

      const smsBody = `${WaiverName} has been submitted, If you need a signed copy of this waiver for your records you may download it by clicking on this link ${url}. Or use your submission ID ${newPushKey}\n\n${process.env.REACT_APP_BASE_URL}`;
      const renterSmsResponse = sendSMS(renterData.phone, smsBody);
      const ownerSmsResponse = sendSMS(ownerData.phone, smsBody);

      setLoading(false);
      navigate(`/waivers/thank-you`, { state: { bookingId: dynamicId } });
    } catch (error) {
      setLoading(false);
      console.error("Error submitting form:", error);
    }
  };

  const [adultWaiverDescription, setAdultWaiverDescription] = useState([]);
  useEffect(() => {
    let cancelled = false;

    const fetchWaiverData = async () => {
      try {
        setPageLoading(true);
        setPageError("");

        const bookingData = await getBookingOnce(dynamicId);
        if (cancelled) return;

        if (!bookingData) {
          setPageError(
            "This waiver link is invalid or the booking could not be found."
          );
          return;
        }

        setBookingData(bookingData);

        const [listing, waiver] = await Promise.all([
          getListingByID(bookingData.listingId),
          getWaiverTemplateByType("adult"),
        ]);
        if (cancelled) return;

        setListingData(listing);

        if (!waiver) {
          setPageError(
            "The waiver form could not be loaded. Please contact waivers@bbrboatrentals.com."
          );
          return;
        }

        setAdultWaiverQuestions(
          waiver.questions.map((question, index) => ({
            ...question,
            id: index,
          }))
        );
        setAdultWaiverDescription(waiver.waiverDescription);

        Promise.all([
          getUserByID(bookingData.owner),
          getUserByID(bookingData.renter),
        ])
          .then(([ownerData, renterData]) => {
            if (cancelled) return;
            if (renterData) {
              setRenterData(renterData);
              setRenterEmail(renterData.email);
            }
            if (ownerData) {
              setOwnerData(ownerData);
              setOwnerEmail(ownerData.email);
            }
          })
          .catch((error) => {
            console.error("Error loading waiver contact info:", error);
          });
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching waiver data:", error);
          setPageError(
            "Unable to load the waiver. Please refresh the page or try again later."
          );
        }
      } finally {
        if (!cancelled) {
          setPageLoading(false);
        }
      }
    };

    fetchWaiverData();

    return () => {
      cancelled = true;
    };
  }, [dynamicId]);

  useEffect(() => {
    const todayDateElement = document.querySelector(".today-date");
    if (todayDateElement) {
      todayDateElement.textContent = "";
    }
  }, [adultWaiverDescription]);

  return (
    <>
      <section className="p-4 d-flex align-items-center">
        <Container className="mt-4 mb-4">
          <Row className="card p-5 d-flex flex-row align-items-center">
            <Col xs={4}>
              <img
                className="img-fluid bg-black"
                src="/assets/backend/images/logo.png"
                style={{ backgroundColor: "#121212" }}
              ></img>
            </Col>
            <Col xs={8}>
              <h3
                className=""
                style={{
                  color: "#27313F",
                  fontWeight: "800",
                  textAlign: "center",
                }}
              >
                BBR BASS BOAT RENTALS, LLC
              </h3>
              <p
                style={{
                  color: "#27313F",
                  fontWeight: "400",
                  fontSize: "19px",
                  textAlign: "center",
                }}
              >
                ADULT PASSENGER WAIVER
              </p>
            </Col>
          </Row>
          <Row>
            <Col className="m-0 p-0 mt-4">
              {pageLoading ? (
                <div className="card p-5 d-flex justify-content-center align-items-center">
                  <ThreeDots
                    visible={true}
                    height="50"
                    width="50"
                    color="#27313F"
                    radius="20"
                    ariaLabel="loading-waiver"
                  />
                </div>
              ) : null}
              {!pageLoading && pageError ? (
                <div className="card p-4 mb-4">
                  <p className="text-danger mb-0">{pageError}</p>
                </div>
              ) : null}
              {/* Captain Selection for Bass Boats (boatCaptain === false means renter drives) */}
              {!pageLoading && !pageError && listingData?.boatCaptain === false && isBoatCaptain === null && (
                <div className="card p-4 mb-4">
                  <h3 className="mb-3">Are you the boat captain for this trip?</h3>
                  <p className="text-muted mb-4">
                    Please select whether you will be operating the boat during this trip.
                  </p>
                  <div className="d-flex gap-3">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => setIsBoatCaptain(true)}
                      style={{ minWidth: "150px" }}
                    >
                      Yes, I am the Captain
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      onClick={() => setIsBoatCaptain(false)}
                      style={{ minWidth: "150px" }}
                    >
                      No, I am a Passenger
                    </Button>
                  </div>
                </div>
              )}

              {/* Message for Boat Captain - they fill waiver at trip start */}
              {!pageLoading && !pageError && listingData?.boatCaptain === false && isBoatCaptain === true && (
                <div className="card p-4 mb-4" style={{ backgroundColor: "#fff3cd", borderColor: "#ffc107" }}>
                  <h3 className="mb-3" style={{ color: "#856404" }}>
                    <i className="bi bi-info-circle me-2"></i>
                    Captain's Waiver Information
                  </h3>
                  <p style={{ color: "#856404", fontSize: "16px", marginBottom: "15px" }}>
                    As the boat captain, you will fill out your waiver at the <strong>start of the trip</strong>.
                  </p>
                  <p style={{ color: "#856404", fontSize: "14px", marginBottom: "20px" }}>
                    The Captain's Waiver & Equipment Checklist will be completed when you begin your trip. 
                    This ensures all safety checks are performed right before departure.
                  </p>
                  {passengerWaiversComplete ? (
                    <p style={{ color: "#856404", fontSize: "14px", marginBottom: "20px" }}>
                      {requiredPassengerWaivers === 0
                        ? "You are the only person on this trip, so no passenger waivers are needed before starting."
                        : "All passenger waivers have been submitted. You can proceed to start your trip."}
                    </p>
                  ) : (
                    <p style={{ color: "#856404", fontSize: "14px", marginBottom: "20px" }}>
                      {requiredPassengerWaivers -
                        (bookingData?.waiversFilled?.adult || 0) -
                        (bookingData?.waiversFilled?.minor || 0)}{" "}
                      passenger waiver(s) still need to be completed before you can start the trip.
                    </p>
                  )}
                  <div className="d-flex gap-3 flex-wrap">
                    <Button
                      variant="outline-warning"
                      onClick={() => setIsBoatCaptain(null)}
                      style={{ color: "#856404", borderColor: "#856404" }}
                    >
                      ← Go Back
                    </Button>
                    {passengerWaiversComplete && (
                      <Button
                        variant="warning"
                        onClick={handleCaptainAcknowledge}
                        disabled={captainAcknowledgeLoading}
                      >
                        {captainAcknowledgeLoading ? (
                          <ThreeDots
                            visible={true}
                            height="22"
                            width="22"
                            color="#856404"
                            radius="20"
                            ariaLabel="three-dots-loading"
                          />
                        ) : (
                          "Continue to Start Trip"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Show waiver form only for non-captain passengers OR when boat has a dedicated captain */}
              {!pageLoading && !pageError && (listingData?.boatCaptain !== false || isBoatCaptain === false) && (
              <form onSubmit={handleSubmit}>
                {adultWaiverQuestions && adultWaiverQuestions !== null ? (
                  <div className="card p-4">
                    <div className="row">
                      <div className="col">
                        <div className="page-description">
                          <h1>Adult Passenger Waiver</h1>
                          <div
                            className="waiverDescrption mt-4"
                            dangerouslySetInnerHTML={{
                              __html: adultWaiverDescription,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                <div className="card p-4 mt-5">
                  {adultWaiverQuestions && adultWaiverQuestions !== null
                    ? adultWaiverQuestions.map((questionData) => (
                        <div
                          key={`${questionData.question}-${questionData.id}`}
                        >
                          {questionData.question === "First Name" ? (
                            <>
                              <h3 className="mt-2">Adult Passenger Name</h3>
                              <label className="form-label form-label mt-2">
                                {questionData.question}:
                              </label>
                            </>
                          ) : questionData.question ===
                            "Adult Passenger Date Of Birth" ? (
                            <>
                              <h3 className="mt-5">
                                Adult Passenger Date Of Birth
                              </h3>
                            </>
                          ) : questionData.question ===
                            "Adult Passenger Signature" ? (
                            <>
                              <h3 className="mt-5 mb-3">
                                Adult Passenger Signature
                              </h3>
                            </>
                          ) : questionData.question === "Email" ? (
                            <>
                              <h3 className="mt-5 mb-3">Email Address</h3>
                              <label className="form-label form-label mt-2">
                                {questionData.question}:
                              </label>
                            </>
                          ) : questionData.question ===
                            "Name of the captain operating the boat" ? (
                            <>
                              <h3 className="mt-5 mb-3">
                                Name of the captain operating the boat:
                              </h3>
                            </>
                          ) : (
                            <label className="form-label form-label mt-2">
                              {questionData.question}:
                            </label>
                          )}
                          {questionData.type === "text" ? (
                            <input
                              required={
                                questionData?.required &&
                                questionData?.required == "true"
                                  ? true
                                  : false
                              }
                              type="text"
                              className="form-control form-control"
                              value={
                                adultFormResponses.find(
                                  (response) =>
                                    response.Question === questionData.id
                                )?.Answer || ""
                              }
                              onChange={(e) =>
                                handleAdultInputChange(
                                  questionData.id,
                                  e.target.value
                                )
                              }
                            />
                          ) : questionData.type === "date" ? (
                            <input
                              required={
                                questionData?.required &&
                                questionData?.required == "true"
                                  ? true
                                  : false
                              }
                              type="date"
                              className="form-control form-control"
                              value={
                                adultFormResponses.find(
                                  (response) => response.Question === "date"
                                )?.Answer || ""
                              }
                              onChange={(e) =>
                                handleAdultInputChange("date", e.target.value)
                              }
                            />
                          ) : questionData.type === "country" ? (
                            <select
                              required={
                                questionData?.required &&
                                questionData?.required == "true"
                                  ? true
                                  : false
                              }
                              className="form-control"
                              name="settingsInputCountry"
                              value={
                                adultFormResponses.find(
                                  (response) => response.Question === "country"
                                )?.Answer || ""
                              }
                              onChange={(e) =>
                                handleAdultInputChange(
                                  "country",
                                  e.target.value
                                )
                              }
                            >
                              <option>United States</option>
                              {/* Add other country options if needed */}
                            </select>
                          ) : questionData.type === "signature" ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                              <SignatureCanvas
                                ref={signatureRef}
                                onBegin={handleSignatureBegin}
                                required={
                                  questionData?.required &&
                                  questionData?.required == "true"
                                    ? true
                                    : false
                                }
                                canvasProps={{
                                  width: isMobile ? 250 : 500,
                                  height: 200,
                                  className: "sigCanvas",
                                }}
                              />
                              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    signatureRef.current.clear();
                                    setSignatureDateTime("");
                                  }}
                                  className="btn btn-primary"
                                >
                                  Clear
                                </button>
                                {signatureDateTime && (
                                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#27313F" }}>
                                    ✅ Signed: {signatureDateTime}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            questionData.options.map((option) => (
                              <div
                                className="btn btn-light m-1 mb-4 form-check"
                                key={option}
                              >
                                <label
                                  htmlFor={option}
                                  className="form-check-label"
                                >
                                  {option}
                                </label>
                                <input
                                  required={
                                    questionData?.required &&
                                    questionData?.required == "true"
                                      ? true
                                      : false
                                  }
                                  type="radio"
                                  name={questionData.id}
                                  value={option}
                                  id={option}
                                  className="form-check-input"
                                  checked={
                                    adultFormResponses.find(
                                      (response) =>
                                        response.Question === questionData.id
                                    )?.Answer === option
                                  }
                                  onChange={() =>
                                    handleAdultInputChange(
                                      questionData.id,
                                      option
                                    )
                                  }
                                />
                              </div>
                            ))
                          )}
                        </div>
                      ))
                    : ""}
                  <button
                    type="submit"
                    className="btn  !md:w-[30%] mt-3 btn-primary mt-3"
                  >
                    {loading ? (
                      <ThreeDots
                        visible={true}
                        height="22"
                        width="22"
                        color="#fff"
                        radius="20"
                        ariaLabel="three-dots-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                      />
                    ) : (
                      "Agree To This Document"
                    )}
                  </button>
                </div>
              </form>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}
